/**
 * Item Cluster Store
 * 
 * Manages grouping of related/duplicate items into clusters.
 * Enables prioritizing the overarching need while preserving individual context.
 * 
 * SUPABASE-ONLY PERSISTENCE:
 * - Primary: Supabase (single source of truth)
 * - No localStorage cache (eliminates stale data issues)
 */

import { create } from 'zustand';
import type { 
  ItemCluster, 
  ClusterMembership, 
  ClusterRelationType,
  ClusterWithMembers,
  DuplicateSuggestion,
  ListPosition 
} from '../../types/prioritization/itemClustering';
import { useItemsStore } from './itemsStore';
import {
  fetchAll,
  insertRecord,
  updateRecord,
  insertMany,
  isSupabaseAvailable,
  createInitialSyncState,
  generateUUID,
  getCurrentWorkspaceId,
  type SyncState,
} from '../../lib/supabaseSync';
import { dispatchStoreError } from '../../hooks/prioritization/useStoreErrorHandler';

const CLUSTERS_TABLE = 'item_clusters';
const MEMBERSHIPS_TABLE = 'cluster_memberships';

// ============================================================================
// Types
// ============================================================================

interface ClusterState {
  // Data
  clusters: ItemCluster[];
  memberships: ClusterMembership[];
  listPositions: ListPosition[];
  duplicateSuggestions: DuplicateSuggestion[];
  
  // Sync state
  syncState: SyncState;
  
  // Supabase sync actions
  fetchFromSupabase: () => Promise<void>;
  syncToSupabase: (cluster: ItemCluster) => Promise<{ success: boolean; error?: string }>;
  
  // Actions - Cluster CRUD
  createCluster: (params: {
    itemIds: string[];
    canonicalItemId: string;
    relationType: ClusterRelationType;
    title?: string;
    description?: string;
    rationale: string;
    createdBy: string;
  }) => ItemCluster;
  
  updateCluster: (clusterId: string, updates: Partial<Pick<ItemCluster, 'title' | 'description' | 'relationType'>>) => void;
  dissolveCluster: (clusterId: string, reason: string) => void;
  
  // Actions - Membership
  addToCluster: (clusterId: string, itemIds: string[], inclusionReason: string, addedBy: string) => void;
  removeFromCluster: (clusterId: string, itemIds: string[], reason: string) => void;
  changeCanonical: (clusterId: string, newCanonicalItemId: string, reason: string) => void;
  
  // Actions - List Positioning
  setListPosition: (itemOrClusterId: string, itemPoolId: string, rank: number, aboveTheLine: boolean, rankedBy: string, rationale?: string) => void;
  removeListPosition: (itemOrClusterId: string, itemPoolId: string) => void;
  
  // Actions - Duplicate Detection
  addDuplicateSuggestion: (suggestion: Omit<DuplicateSuggestion, 'reviewedAt' | 'reviewedBy' | 'reviewDecision'>) => void;
  reviewDuplicateSuggestion: (itemId: string, decision: 'accepted' | 'rejected' | 'deferred', reviewedBy: string) => void;
  
  // Getters
  getClusterById: (clusterId: string) => ItemCluster | undefined;
  getClusterForItem: (itemId: string) => ItemCluster | undefined;
  getClusterWithMembers: (clusterId: string) => ClusterWithMembers | undefined;
  getMembersOfCluster: (clusterId: string) => ClusterMembership[];
  getListPositions: (itemOrClusterId: string) => ListPosition[];
  getPositionInList: (itemOrClusterId: string, itemPoolId: string) => ListPosition | undefined;
  getPendingSuggestions: () => DuplicateSuggestion[];
  
  // Utilities
  isItemInCluster: (itemId: string) => boolean;
  isCanonicalItem: (itemId: string) => boolean;
  
  // Demo data
  loadDemoData: () => void;
}

// ============================================================================
// Store Implementation
// ============================================================================

export const useClusterStore = create<ClusterState>()(
  (set, get) => ({
    clusters: [],
    memberships: [],
    listPositions: [],
      duplicateSuggestions: [],
      syncState: createInitialSyncState(),
      
      // ======================================================================
      // Supabase Sync Actions
      // ======================================================================
      
      fetchFromSupabase: async () => {
        if (!isSupabaseAvailable()) {
          console.log('[ClusterStore] Supabase not available');
          return;
        }
        
        set({ syncState: { ...get().syncState, isLoading: true, syncError: null } });
        
        // item_clusters table has no workspace_id column; fetch all and rely on RLS/canonical_item_id for scope
        const [clustersResult, membershipsResult] = await Promise.all([
          fetchAll<ItemCluster>(CLUSTERS_TABLE, { column: 'created_at', ascending: false }, { filterByWorkspace: false }),
          fetchAll<ClusterMembership>(MEMBERSHIPS_TABLE, { column: 'added_at', ascending: false }, { filterByWorkspace: false }),
        ]);
        
        if (clustersResult.error || membershipsResult.error) {
          set({ 
            syncState: { 
              ...get().syncState, 
              isLoading: false, 
              syncError: clustersResult.error || membershipsResult.error || 'Unknown error'
            } 
          });
          console.error('[ClusterStore] Failed to fetch from Supabase:', clustersResult.error || membershipsResult.error);
          return;
        }
        
        const rawClusters = clustersResult.data || [];
        const newMemberships = membershipsResult.data || [];
        
        // Hydrate clusters with memberItemIds from the memberships table
        const allHydrated: ItemCluster[] = rawClusters.map(cluster => {
          const clusterMemberships = newMemberships.filter(m => m.clusterId === cluster.id);
          return {
            ...cluster,
            memberItemIds: clusterMemberships.map(m => m.itemId),
          };
        });

        // Workspace-scope: only keep clusters whose items belong to the current workspace.
        // Default to empty when no workspace is set or items haven't loaded yet to prevent
        // cross-workspace data leakage.
        const workspaceId = getCurrentWorkspaceId();
        const workspaceItems = workspaceId
          ? useItemsStore.getState().items.filter(i => i.workspaceId === workspaceId)
          : [];
        const workspaceItemIds = new Set(workspaceItems.map(i => i.id));
        const hydratedClusters = workspaceId
          ? allHydrated.filter(c => workspaceItemIds.has(c.canonicalItemId) || c.memberItemIds.some(id => workspaceItemIds.has(id)))
          : [];
        
        // MERGE strategy: preserve local clusters that aren't in Supabase yet
        const existingClusters = get().clusters;
        const existingMemberships = get().memberships;
        
        const supabaseClusterIds = new Set(hydratedClusters.map(c => c.id));
        const supabaseMembershipIds = new Set(newMemberships.map(m => m.id));
        
        const localOnlyClusters = existingClusters.filter(c => !supabaseClusterIds.has(c.id));
        const localOnlyMemberships = existingMemberships.filter(m => !supabaseMembershipIds.has(m.id));
        
        const mergedClusters = [...hydratedClusters, ...localOnlyClusters];
        const mergedMemberships = [...newMemberships, ...localOnlyMemberships];
        
        set({ 
          clusters: mergedClusters,
          memberships: mergedMemberships,
          syncState: { 
            ...get().syncState, 
            isLoading: false, 
            lastSyncAt: new Date(),
            syncError: null 
          }
        });
        console.log(`[ClusterStore] Loaded ${hydratedClusters.length} from Supabase, kept ${localOnlyClusters.length} local-only. Total: ${mergedClusters.length}`);
      },
      
      syncToSupabase: async (cluster: ItemCluster) => {
        if (!isSupabaseAvailable()) {
          return { success: true };
        }
        
        set({ syncState: { ...get().syncState, isSyncing: true } });
        
        const result = await updateRecord<ItemCluster>(CLUSTERS_TABLE, cluster.id, cluster);
        
        set({ syncState: { ...get().syncState, isSyncing: false } });
        
        if (result.error) {
          console.error('[ClusterStore] Failed to sync to Supabase:', result.error);
          return { success: false, error: result.error };
        }
        
        return { success: true };
      },
      
      // ======================================================================
      // Cluster CRUD
      // ======================================================================
      
      createCluster: (params) => {
        const { itemIds, canonicalItemId, relationType, title, description, rationale, createdBy } = params;
        
        // Validate canonical item is in the list
        if (!itemIds.includes(canonicalItemId)) {
          throw new Error('Canonical item must be one of the member items');
        }
        
        // Get item details for title fallback
        const itemsStore = useItemsStore.getState();
        const canonicalItem = itemsStore.getItemById(canonicalItemId);
        
        const now = new Date();
        const clusterId = generateUUID();
        
        const cluster: ItemCluster = {
          id: clusterId,
          canonicalItemId,
          title: title || canonicalItem?.title || 'Untitled Cluster',
          description,
          memberItemIds: itemIds,
          relationType,
          clusteringRationale: rationale,
          createdAt: now,
          createdBy,
          updatedAt: now,
          status: 'active',
        };
        
        // Create memberships for each item
        const newMemberships: ClusterMembership[] = itemIds.map(itemId => {
          const item = itemsStore.getItemById(itemId);
          return {
            clusterId,
            itemId,
            isCanonical: itemId === canonicalItemId,
            originalTitle: item?.title || 'Unknown',
            originalDescription: item?.description,
            submittedBy: item?.createdBy || 'unknown',
            submittedAt: item?.createdAt || now,
            addedAt: now,
            addedBy: createdBy,
          };
        });
        
        // Update local state first (optimistic update)
        set(state => ({
          clusters: [...state.clusters, cluster],
          memberships: [...state.memberships, ...newMemberships],
        }));
        
        // Then sync to Supabase: insert cluster first, then memberships (FK dependency)
        if (isSupabaseAvailable()) {
          insertRecord<ItemCluster>(CLUSTERS_TABLE, cluster).then(result => {
            if (result.error) {
              console.error('[ClusterStore] Failed to insert cluster to Supabase:', result.error);
              set(state => ({
                clusters: state.clusters.filter(c => c.id !== clusterId),
                memberships: state.memberships.filter(m => m.clusterId !== clusterId),
              }));
              return;
            }
            insertMany<ClusterMembership>(MEMBERSHIPS_TABLE, newMemberships).then(mResult => {
              if (mResult.error) {
                console.error('[ClusterStore] Failed to insert memberships to Supabase:', mResult.error);
                dispatchStoreError('Clusters', 'add cluster members', mResult.error);
              }
            });
          });
        }
        
        return cluster;
      },
      
      updateCluster: (clusterId, updates) => {
        const cluster = get().clusters.find(c => c.id === clusterId);
        if (!cluster) return;
        
        const updated = { ...cluster, ...updates, updatedAt: new Date() };
        
        // Update local state first (optimistic update)
        set(state => ({
          clusters: state.clusters.map(c => c.id === clusterId ? updated : c),
        }));
        
        // Then sync to Supabase
        if (isSupabaseAvailable()) {
          updateRecord<ItemCluster>(CLUSTERS_TABLE, clusterId, updated).then(result => {
            if (result.error) {
              console.error('[ClusterStore] Failed to update cluster in Supabase:', result.error);
              dispatchStoreError('Clusters', 'update cluster', result.error);
            }
          });
        }
      },
      
      dissolveCluster: (clusterId, _reason) => {
        const cluster = get().clusters.find(c => c.id === clusterId);
        if (!cluster) return;
        
        const updated = { ...cluster, status: 'dissolved' as const, updatedAt: new Date() };
        
        // Update local state first (optimistic update)
        set(state => ({
          clusters: state.clusters.map(c => c.id === clusterId ? updated : c),
          // Keep memberships for history, but cluster is dissolved
        }));
        
        // Then sync to Supabase
        if (isSupabaseAvailable()) {
          updateRecord<ItemCluster>(CLUSTERS_TABLE, clusterId, updated).then(result => {
            if (result.error) {
              console.error('[ClusterStore] Failed to dissolve cluster in Supabase:', result.error);
              dispatchStoreError('Clusters', 'dissolve cluster', result.error);
            }
          });
        }
      },
      
      // ======================================================================
      // Membership Management
      // ======================================================================
      
      addToCluster: (clusterId, itemIds, inclusionReason, addedBy) => {
        const cluster = get().clusters.find(c => c.id === clusterId);
        if (!cluster || cluster.status === 'dissolved') {
          throw new Error('Cluster not found or dissolved');
        }
        
        const itemsStore = useItemsStore.getState();
        const now = new Date();
        
        const newMemberships: ClusterMembership[] = itemIds
          .filter(itemId => !cluster.memberItemIds.includes(itemId))
          .map(itemId => {
            const item = itemsStore.getItemById(itemId);
            return {
              clusterId,
              itemId,
              isCanonical: false,
              originalTitle: item?.title || 'Unknown',
              originalDescription: item?.description,
              submittedBy: item?.createdBy || 'unknown',
              submittedAt: item?.createdAt || now,
              inclusionReason,
              addedAt: now,
              addedBy,
            };
          });
        
        set(state => ({
          clusters: state.clusters.map(c =>
            c.id === clusterId
              ? { 
                  ...c, 
                  memberItemIds: [...c.memberItemIds, ...itemIds.filter(id => !c.memberItemIds.includes(id))],
                  updatedAt: now,
                }
              : c
          ),
          memberships: [...state.memberships, ...newMemberships],
        }));
      },
      
      removeFromCluster: (clusterId, itemIds, _reason) => {
        const cluster = get().clusters.find(c => c.id === clusterId);
        if (!cluster) return;
        
        // Can't remove canonical item
        if (itemIds.includes(cluster.canonicalItemId)) {
          throw new Error('Cannot remove canonical item. Change canonical first.');
        }
        
        set(state => ({
          clusters: state.clusters.map(c =>
            c.id === clusterId
              ? {
                  ...c,
                  memberItemIds: c.memberItemIds.filter(id => !itemIds.includes(id)),
                  updatedAt: new Date(),
                }
              : c
          ),
          memberships: state.memberships.filter(m => 
            !(m.clusterId === clusterId && itemIds.includes(m.itemId))
          ),
        }));
      },
      
      changeCanonical: (clusterId, newCanonicalItemId, _reason) => {
        const cluster = get().clusters.find(c => c.id === clusterId);
        if (!cluster) return;
        
        if (!cluster.memberItemIds.includes(newCanonicalItemId)) {
          throw new Error('New canonical item must be a member of the cluster');
        }
        
        set(state => ({
          clusters: state.clusters.map(c =>
            c.id === clusterId
              ? { ...c, canonicalItemId: newCanonicalItemId, updatedAt: new Date() }
              : c
          ),
          memberships: state.memberships.map(m =>
            m.clusterId === clusterId
              ? { ...m, isCanonical: m.itemId === newCanonicalItemId }
              : m
          ),
        }));
      },
      
      // ======================================================================
      // List Positioning
      // ======================================================================
      
      setListPosition: (itemOrClusterId, itemPoolId, rank, aboveTheLine, rankedBy, rationale) => {
        const now = new Date();
        const existing = get().listPositions.find(
          p => p.itemId === itemOrClusterId && p.itemPoolId === itemPoolId
        );
        
        if (existing) {
          set(state => ({
            listPositions: state.listPositions.map(p =>
              p.itemId === itemOrClusterId && p.itemPoolId === itemPoolId
                ? { ...p, rank, aboveTheLine, rankedBy, rankedAt: now, positionRationale: rationale }
                : p
            ),
          }));
        } else {
          const newPosition: ListPosition = {
            itemId: itemOrClusterId,
            itemPoolId,
            rank,
            aboveTheLine,
            rankedBy,
            rankedAt: now,
            positionRationale: rationale,
          };
          set(state => ({
            listPositions: [...state.listPositions, newPosition],
          }));
        }
      },
      
      removeListPosition: (itemOrClusterId, itemPoolId) => {
        set(state => ({
          listPositions: state.listPositions.filter(
            p => !(p.itemId === itemOrClusterId && p.itemPoolId === itemPoolId)
          ),
        }));
      },
      
      // ======================================================================
      // Duplicate Detection
      // ======================================================================
      
      addDuplicateSuggestion: (suggestion) => {
        set(state => ({
          duplicateSuggestions: [...state.duplicateSuggestions, suggestion],
        }));
      },
      
      reviewDuplicateSuggestion: (itemId, decision, reviewedBy) => {
        set(state => ({
          duplicateSuggestions: state.duplicateSuggestions.map(s =>
            s.itemId === itemId
              ? { ...s, reviewedAt: new Date(), reviewedBy, reviewDecision: decision }
              : s
          ),
        }));
      },
      
      // ======================================================================
      // Getters
      // ======================================================================
      
      getClusterById: (clusterId) => {
        return get().clusters.find(c => c.id === clusterId && c.status === 'active');
      },
      
      getClusterForItem: (itemId) => {
        return get().clusters.find(
          c => c.status === 'active' && c.memberItemIds.includes(itemId)
        );
      },
      
      getClusterWithMembers: (clusterId) => {
        const cluster = get().clusters.find(c => c.id === clusterId);
        if (!cluster) return undefined;
        
        const itemsStore = useItemsStore.getState();
        const canonicalItem = itemsStore.getItemById(cluster.canonicalItemId);
        const memberships = get().memberships.filter(m => m.clusterId === clusterId);
        
        const members = memberships.map(m => ({
          id: m.itemId,
          title: m.originalTitle,
          submittedBy: m.submittedBy,
          submittedAt: m.submittedAt,
          isCanonical: m.isCanonical,
        }));
        
        const uniqueSubmitters = new Set(members.map(m => m.submittedBy)).size;
        const dates = members.map(m => m.submittedAt);
        
        return {
          ...cluster,
          canonicalItem: {
            id: cluster.canonicalItemId,
            title: canonicalItem?.title || cluster.title,
            description: canonicalItem?.description,
          },
          members,
          stats: {
            memberCount: members.length,
            uniqueSubmitters,
            earliestSubmission: new Date(Math.min(...dates.map(d => d.getTime()))),
            latestSubmission: new Date(Math.max(...dates.map(d => d.getTime()))),
          },
        };
      },
      
      getMembersOfCluster: (clusterId) => {
        return get().memberships.filter(m => m.clusterId === clusterId);
      },
      
      getListPositions: (itemOrClusterId) => {
        return get().listPositions.filter(p => p.itemId === itemOrClusterId);
      },
      
      getPositionInList: (itemOrClusterId, itemPoolId) => {
        return get().listPositions.find(
          p => p.itemId === itemOrClusterId && p.itemPoolId === itemPoolId
        );
      },
      
      getPendingSuggestions: () => {
        return get().duplicateSuggestions.filter(s => !s.reviewDecision);
      },
      
      // ======================================================================
      // Utilities
      // ======================================================================
      
      isItemInCluster: (itemId) => {
        return get().clusters.some(
          c => c.status === 'active' && c.memberItemIds.includes(itemId)
        );
      },
      
      isCanonicalItem: (itemId) => {
        return get().clusters.some(
          c => c.status === 'active' && c.canonicalItemId === itemId
        );
      },
      
      // ======================================================================
      // Demo Data
      // ======================================================================
      
      loadDemoData: () => {
        const now = new Date();
        
        // Create demo cluster
        const demoCluster: ItemCluster = {
          id: 'cluster-demo-1',
          canonicalItemId: 'demo-item-1',
          title: 'Mobile App Performance Issues',
          description: 'Multiple reports of slow performance and crashes on mobile platforms',
          memberItemIds: ['demo-item-1', 'demo-item-2', 'demo-item-3', 'demo-item-4'],
          relationType: 'duplicate',
          clusteringRationale: 'All items describe the same core issue: mobile app performance',
          createdAt: now,
          createdBy: 'demo-user',
          updatedAt: now,
          status: 'active',
        };
        
        const demoMemberships: ClusterMembership[] = [
          {
            clusterId: 'cluster-demo-1',
            itemId: 'demo-item-1',
            isCanonical: true,
            originalTitle: 'App is slow on Android',
            originalDescription: 'The app takes forever to load on my Android phone',
            submittedBy: 'sarah@engineering.com',
            submittedAt: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000),
            addedAt: now,
            addedBy: 'demo-user',
          },
          {
            clusterId: 'cluster-demo-1',
            itemId: 'demo-item-2',
            isCanonical: false,
            originalTitle: 'Android app crashes on startup',
            originalDescription: 'Customers reporting app crashes immediately after opening',
            submittedBy: 'mike@support.com',
            submittedAt: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000),
            inclusionReason: 'Same root cause as performance issues',
            addedAt: now,
            addedBy: 'demo-user',
          },
          {
            clusterId: 'cluster-demo-1',
            itemId: 'demo-item-3',
            isCanonical: false,
            originalTitle: 'Mobile performance regression',
            originalDescription: 'Recent release caused significant slowdown',
            submittedBy: 'lisa@product.com',
            submittedAt: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000),
            inclusionReason: 'Identified as same issue',
            addedAt: now,
            addedBy: 'demo-user',
          },
          {
            clusterId: 'cluster-demo-1',
            itemId: 'demo-item-4',
            isCanonical: false,
            originalTitle: 'App unusable on older devices',
            originalDescription: 'QA found app is extremely slow on devices 2+ years old',
            submittedBy: 'qa-team@company.com',
            submittedAt: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000),
            inclusionReason: 'Related performance issue',
            addedAt: now,
            addedBy: 'demo-user',
          },
        ];
        
        // Demo positions in different lists
        const demoPositions: ListPosition[] = [
          {
            itemId: 'cluster-demo-1',
            itemPoolId: 'list-engineering',
            rank: 2,
            aboveTheLine: true,
            rankedBy: 'eng-lead',
            rankedAt: now,
            positionRationale: 'Critical for user retention',
          },
          {
            itemId: 'cluster-demo-1',
            itemPoolId: 'list-product',
            rank: 5,
            aboveTheLine: true,
            rankedBy: 'product-manager',
            rankedAt: now,
            positionRationale: 'Important but feature work takes priority',
          },
        ];
        
        set({
        clusters: [demoCluster],
        memberships: demoMemberships,
        listPositions: demoPositions,
        duplicateSuggestions: [],
      });
    },
  })
);

// ============================================================================
// Convenience Hooks
// ============================================================================

/**
 * IMPORTANT: These hooks return raw state to avoid Zustand infinite loop issues.
 * Filter/derive data using useMemo in your component instead of inline selectors.
 * 
 * Example:
 *   const allClusters = useAllClusters();
 *   const activeClusters = useMemo(() => allClusters.filter(c => c.status === 'active'), [allClusters]);
 */

// Get all clusters - filter with useMemo in component
export const useAllClusters = () => useClusterStore(state => state.clusters);

// Get all duplicate suggestions - filter with useMemo in component  
export const useAllDuplicateSuggestions = () => useClusterStore(state => state.duplicateSuggestions);

// For item-specific lookups, use .find() which returns a single value (not an array)
export const useClusterForItem = (itemId: string) => useClusterStore(
  (state) => state.clusters.find(c => c.status === 'active' && c.memberItemIds.includes(itemId))
);
